import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from "react";
import type { CatalogShop, InventoryItem, VisitorAccess, WorldManifest } from "@townsy/contracts";
import type { LayoutOutcome } from "../../homes/commands";
import {
  anchorLabel,
  anchorsAcceptingItem,
  anchorsForScope,
  catalogItemName,
  fittingRotations,
  freeAnchors,
  groupAnchors,
  itemsFittingAnchor,
  placedInScope,
  rotationLabel,
  stageLabel,
  storedItems,
  type DecorScope,
  type MyHome,
} from "../../homes/homeLogic";
import { EMPTY_DRAFT, PreviewSync, draftHighlight, draftPreview, normalizeDraft, type PlacementDraft, type PreviewSink } from "../../homes/placementDraft";
import { Dialog } from "../Dialog";
import { SENT_OUT_MESSAGE, SendVisitorsOutView, VisitorAccessSwitch, accessChangedMessage } from "../VisitorAccess";
import type { HomeActions } from "./useHomeData";

/**
 * "My home" (docs/01 section 6 "Home management"): the owner places stored items on free predefined
 * spots, rotates and removes them, resets the layout (everything back to storage, nothing deleted)
 * and opens or closes the home to visitors (ADR-0019; "Send visitors out"). Choosing a spot marks it in the 3D town; choosing an
 * item and rotation shows a translucent, clearly uncommitted ghost; only "Place" asks the server,
 * and the ghost disappears once the server confirms. The same view works in the 2D directory, where
 * the preview is the text line under the choices.
 */
export interface TownPlacementContext {
  /** The avatar is inside this home's one room (furniture can be placed and previewed). */
  insideHome: boolean;
  /** The avatar is on the street (the yard ghost is visible there). */
  onStreet: boolean;
  transitionPending: boolean;
  /** Why travelling into the home is not possible right now (e.g. driving a car: get out first), or null. */
  travelBlockedReason?: string | null;
  onEnterHome(): void;
  onShowYard(): void;
}

export interface MyHomeManagerProps {
  home: MyHome;
  inventory: readonly InventoryItem[];
  catalogItems: ReadonlyArray<{ itemId: string; name: string }> | null;
  manifest: WorldManifest;
  tab: DecorScope;
  onTab(tab: DecorScope): void;
  actions: Pick<HomeActions, "place" | "remove" | "resetLayout" | "setAccess" | "sendVisitorsOut">;
  /**
   * ADR-0019: how many other people are inside this home, from the room the owner's client is in (null when the owner
   * is not inside, e.g. on the street or in the 2D directory: unknown).
   */
  visitorsInside?: number | null;
  /** Renderer bridge; null in the 2D directory. */
  sink: PreviewSink | null;
  /** Changes whenever the renderer rebuilds its room, so the ghost is re-sent. */
  epoch: string;
  /** In-town shortcuts; null in the 2D directory. */
  town: TownPlacementContext | null;
  /** "Buy one at …" (button in town, link on the 2D page). */
  shopAction(shop: CatalogShop): ReactNode;
}

type Message = { tone: "success" | "warning" | "danger"; text: string };

/**
 * "Place at home" from outside the home (founder report 2026-10-14: "When I click PLACE IT nothing happens" — the owner
 * was in Townsy Furnishings): the item to start placing once the owner arrives inside. Module scope so it survives the
 * panel re-rendering or remounting during the room change; cleared when used.
 */
let pendingPlacement: { propertyId: string; inventoryInstanceId: string } | null = null;

export function MyHomeManager({ home, inventory, catalogItems, manifest, tab, onTab, actions, visitorsInside, sink, epoch, town, shopAction }: MyHomeManagerProps) {
  const uid = useId();
  const [rawDraft, setRawDraft] = useState<PlacementDraft>(EMPTY_DRAFT);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<Message | null>(null);
  const [confirmReset, setConfirmReset] = useState(false);
  const syncRef = useRef<PreviewSync | null>(null);
  const previewRef = useRef<HTMLDivElement | null>(null);

  const anchors = useMemo(() => anchorsForScope(manifest, home.propertyId, tab), [manifest, home.propertyId, tab]);
  const placed = placedInScope(home.placedItems, tab);
  const draft = normalizeDraft(rawDraft, { anchors, placed: home.placedItems, inventory, scope: tab });
  const preview = draftPreview(home.propertyId, tab, draft, inventory);
  const highlight = draftHighlight(home.propertyId, tab, draft, { anchors, placed: home.placedItems });
  const groups = groupAnchors(anchors, home.placedItems, tab);
  const chosenAnchor = draft.anchorId ? (anchors.find((a) => a.id === draft.anchorId) ?? null) : null;
  const fitting = chosenAnchor ? itemsFittingAnchor(chosenAnchor, inventory) : [];
  const chosenItem = draft.inventoryInstanceId ? (inventory.find((i) => i.inventoryInstanceId === draft.inventoryInstanceId) ?? null) : null;
  const rotations = chosenAnchor && chosenItem ? fittingRotations(chosenAnchor, chosenItem.itemId) : [];
  const kind = tab === "yard" ? "YARD" : "FURNITURE";
  const stored = storedItems(inventory).filter((i) => i.kind === kind);
  const name = (itemId: string) => catalogItemName(itemId, catalogItems);
  const shop: CatalogShop = tab === "yard" ? "home_builders" : "furnishings";

  // Where the ghost can be seen: the yard from the street, furniture only from inside this home.
  const canSee = town ? (tab === "yard" ? town.onStreet : town.insideHome) : false;
  const placeBlocked = town && tab === "interior" && !town.insideHome ? "Go inside to place furniture." : null;

  // Keep the renderer's ghost and markers equal to the draft (deduplicated; re-sent after a room rebuild).
  const previewKey = JSON.stringify([canSee ? preview : null, canSee ? highlight : null, epoch]);
  useEffect(() => {
    if (!sink) return;
    if (!syncRef.current) syncRef.current = new PreviewSync(sink);
    const [p, h, e] = JSON.parse(previewKey) as [typeof preview, typeof highlight | null, string];
    syncRef.current.show(p, h, e);
  }, [sink, previewKey]);
  // Closing the manager (unmount) always removes the ghost and the markers.
  useEffect(() => () => syncRef.current?.clear(), []);

  // Placing yard items: frame the home in the overview camera when the Yard tab opens on the street.
  const onShowYard = town?.onShowYard;
  const onStreet = town?.onStreet ?? false;
  useEffect(() => {
    // Only when the tab (or the room) changes, never on every render: onShowYard is deliberately not a dependency.
    if (tab === "yard" && onStreet) onShowYard?.();
  }, [tab, onStreet]);

  const switchTab = (next: DecorScope) => {
    if (next === tab) return;
    setRawDraft(EMPTY_DRAFT);
    setMessage(null);
    onTab(next);
  };

  const settle = (outcome: LayoutOutcome, success: string, opts: { keepDraftOnError?: boolean } = {}) => {
    if (outcome.ok) {
      setMessage({ tone: "success", text: success });
      return true;
    }
    setMessage({ tone: outcome.conflict ? "warning" : "danger", text: outcome.message });
    if (outcome.conflict || !opts.keepDraftOnError) setRawDraft(EMPTY_DRAFT);
    return false;
  };

  const place = async () => {
    if (!preview || !chosenItem || busy || placeBlocked) return;
    setBusy(true);
    setMessage(null);
    const outcome = await actions.place(home, { inventoryInstanceId: chosenItem.inventoryInstanceId, scope: tab, anchorId: preview.anchorId, rotation: preview.rotation });
    setBusy(false);
    if (outcome.ok) {
      // Confirmed: the ghost goes away and the real item takes its place.
      setRawDraft(EMPTY_DRAFT);
      syncRef.current?.show(null, canSee ? draftHighlight(home.propertyId, tab, EMPTY_DRAFT, { anchors, placed: outcome.state.placedItems }) : null, epoch);
    }
    settle(outcome, `Placed ${name(chosenItem.itemId)} at ${anchorLabel(preview.anchorId)}.`, { keepDraftOnError: true });
  };

  const instanceAt = (anchorId: string) =>
    inventory.find((i) => i.placement?.propertyId === home.propertyId && i.placement.scope === tab && i.placement.anchorId === anchorId) ?? null;

  const remove = async (anchorId: string, itemId: string) => {
    if (busy) return;
    setBusy(true);
    setMessage(null);
    const outcome = await actions.remove(home, tab, anchorId);
    setBusy(false);
    settle(outcome, `${name(itemId)} is back in storage.`);
  };

  /** Rotate = back to storage, then place again with the next allowed rotation (both versioned). */
  const rotate = async (anchorId: string, itemId: string, rotation: number) => {
    const anchor = anchors.find((a) => a.id === anchorId);
    const instance = instanceAt(anchorId);
    if (!anchor || !instance || busy) return;
    const options = fittingRotations(anchor, itemId);
    if (options.length < 2) return;
    const idx = options.findIndex((r) => Math.abs(r - rotation) < 1e-3);
    const nextRotation = options[(idx + 1) % options.length]!;
    setBusy(true);
    setMessage(null);
    const removed = await actions.remove(home, tab, anchorId);
    if (!removed.ok) {
      setBusy(false);
      settle(removed, "");
      return;
    }
    const replaced = await actions.place(
      { ...home, layoutVersion: removed.state.layoutVersion, placedItems: removed.state.placedItems },
      { inventoryInstanceId: instance.inventoryInstanceId, scope: tab, anchorId, rotation: nextRotation },
    );
    setBusy(false);
    if (!replaced.ok) {
      setMessage({ tone: "warning", text: `${name(itemId)} went back to storage but could not be placed again: ${replaced.message} Nothing was deleted.` });
      return;
    }
    settle(replaced, `${name(itemId)} now ${rotationLabel(nextRotation).toLowerCase()}.`);
  };

  const reset = async () => {
    setConfirmReset(false);
    if (busy) return;
    setBusy(true);
    setMessage(null);
    const outcome = await actions.resetLayout(home);
    setBusy(false);
    settle(outcome, "Every item is back in storage. Nothing was deleted.");
  };

  /** ADR-0019: Open / Closed. Closing moves nobody out (that is "Send visitors out"). */
  const changeAccess = async (access: VisitorAccess) => {
    if (busy || access === home.access) return;
    setBusy(true);
    setMessage(null);
    const outcome = await actions.setAccess(home, access);
    setBusy(false);
    settle(outcome, accessChangedMessage("home", access), { keepDraftOnError: true });
  };

  const sendVisitorsOut = async () => {
    if (busy) return;
    setBusy(true);
    setMessage(null);
    const outcome = await actions.sendVisitorsOut(home);
    setBusy(false);
    setMessage(outcome.ok ? { tone: "success", text: SENT_OUT_MESSAGE } : { tone: "danger", text: outcome.message });
  };

  /**
   * "Place…" from the storage list: preselect the first free spot that takes this item (the preview and its Place button
   * scroll into view). From outside the home, furniture is placed inside it: go home first, then start there.
   */
  const startWith = (item: InventoryItem) => {
    if (placeBlocked && town) {
      if (town.transitionPending || town.travelBlockedReason) {
        setMessage({ tone: "warning", text: town.travelBlockedReason ?? "Moving… try again in a moment." });
        return;
      }
      pendingPlacement = { propertyId: home.propertyId, inventoryInstanceId: item.inventoryInstanceId };
      setMessage({ tone: "success", text: `Taking you home to place the ${name(item.itemId).toLowerCase()}…` });
      town.onEnterHome();
      return;
    }
    const spot = anchorsAcceptingItem(freeAnchors(anchors, home.placedItems, tab), item.itemId)[0];
    if (!spot) {
      setMessage({ tone: "warning", text: `Every spot that takes ${name(item.itemId)} is in use. Remove something first.` });
      return;
    }
    setMessage(null);
    setRawDraft({ anchorId: spot.id, inventoryInstanceId: item.inventoryInstanceId, rotation: null });
  };

  // Arrived home after "Place at home": start placing that item now.
  const insideHome = town?.insideHome ?? false;
  useEffect(() => {
    const pending = pendingPlacement;
    if (!insideHome || !pending || pending.propertyId !== home.propertyId || tab !== "interior") return;
    const item = inventory.find((i) => i.inventoryInstanceId === pending.inventoryInstanceId);
    pendingPlacement = null;
    if (item) startWith(item);
    // startWith reads the current props; only the arrival (and the item list being loaded) should trigger this.
  }, [insideHome, home.propertyId, tab, inventory]);

  // A chosen item: bring the preview and its Place button into view (the storage list is far below it).
  const chosenId = chosenItem?.inventoryInstanceId ?? null;
  useEffect(() => {
    if (!chosenId) return;
    previewRef.current?.scrollIntoView?.({ block: "nearest", behavior: "smooth" });
    previewRef.current?.querySelector<HTMLButtonElement>("[data-testid=place-confirm]:not(:disabled)")?.focus({ preventScroll: true });
  }, [chosenId]);

  const totalPlaced = home.placedItems.length;

  return (
    <div className="stack my-home" data-testid="my-home-manager">
      <div className="row" style={{ justifyContent: "space-between" }}>
        <span className="small">
          <strong>{home.address}</strong> · {stageLabel(home.stage)}
        </span>
        <span className="small muted" data-testid="my-home-access">
          {home.access === "closed" ? "Closed to visitors" : "Open to visitors"}
        </span>
      </div>
      <div className="tabs" role="tablist" aria-label="Where to place items">
        <button type="button" role="tab" className="tab" aria-selected={tab === "yard"} onClick={() => switchTab("yard")} data-testid="my-home-tab-yard">
          Yard
        </button>
        <button type="button" role="tab" className="tab" aria-selected={tab === "interior"} onClick={() => switchTab("interior")} data-testid="my-home-tab-interior">
          Inside
        </button>
      </div>

      {town && tab === "yard" ? (
        <div className="row small">
          {town.onStreet ? <span className="muted">Your yard is framed in the town view.</span> : <span>Your yard is outside.</span>}
          <button type="button" className="btn btn-sm" disabled={town.transitionPending} onClick={town.onShowYard} data-testid="show-yard">
            Show my yard
          </button>
        </div>
      ) : null}
      {placeBlocked && town ? (
        <div className="notice notice-info small" data-testid="go-inside">
          Go inside to place furniture.{" "}
          <button
            type="button"
            className="btn btn-sm btn-primary"
            disabled={town.transitionPending || Boolean(town.travelBlockedReason)}
            title={town.travelBlockedReason ?? undefined}
            onClick={town.onEnterHome}
            data-testid="enter-my-home"
          >
            {town.transitionPending ? "Moving…" : "Enter my home"}
          </button>
          {town.travelBlockedReason ? <span className="hint"> {town.travelBlockedReason}</span> : null}
        </div>
      ) : null}

      <fieldset className="choice-group" data-movement-block="true" disabled={busy}>
        <legend>1. Choose a free spot</legend>
        {groups.length === 0 ? <p className="muted small">This home has no spots here.</p> : null}
        {groups.map((g) => (
          <div key={g.type} className="anchor-group">
            <div className="small muted">{g.label}</div>
            <div className="choice-options">
              {g.anchors.map(({ anchor, occupiedBy }) => (
                <label key={anchor.id} className="choice choice-pill" title={occupiedBy ? `In use: ${name(occupiedBy.itemId)}` : "Free"}>
                  <input
                    type="radio"
                    className="choice-input"
                    name={`${uid}-anchor`}
                    value={anchor.id}
                    checked={draft.anchorId === anchor.id}
                    disabled={Boolean(occupiedBy)}
                    onChange={() => {
                      setMessage(null);
                      setRawDraft({ anchorId: anchor.id, inventoryInstanceId: null, rotation: null });
                    }}
                    data-testid={`anchor-${anchor.id}`}
                  />
                  <span className="choice-text">
                    {anchorLabel(anchor.id)}
                    {occupiedBy ? ` · ${name(occupiedBy.itemId)}` : ""}
                  </span>
                </label>
              ))}
            </div>
          </div>
        ))}
      </fieldset>

      {chosenAnchor ? (
        <fieldset className="choice-group" data-movement-block="true" disabled={busy}>
          <legend>2. Choose an item from storage</legend>
          {fitting.length === 0 ? (
            <div className="small" data-testid="nothing-fits">
              Nothing in your storage fits {anchorLabel(chosenAnchor.id)}. {shopAction(shop)}
            </div>
          ) : (
            <div className="choice-options">
              {fitting.map((item) => (
                <label key={item.inventoryInstanceId} className="choice choice-pill">
                  <input
                    type="radio"
                    className="choice-input"
                    name={`${uid}-item`}
                    value={item.inventoryInstanceId}
                    checked={draft.inventoryInstanceId === item.inventoryInstanceId}
                    onChange={() => setRawDraft({ anchorId: chosenAnchor.id, inventoryInstanceId: item.inventoryInstanceId, rotation: null })}
                    data-testid={`fit-${item.itemId}`}
                  />
                  <span className="choice-text">{name(item.itemId)}</span>
                </label>
              ))}
            </div>
          )}
        </fieldset>
      ) : null}

      {chosenItem && rotations.length > 1 ? (
        <fieldset className="choice-group" data-movement-block="true" disabled={busy}>
          <legend>3. Rotation</legend>
          <div className="choice-options">
            {rotations.map((r) => (
              <label key={r} className="choice choice-pill">
                <input
                  type="radio"
                  className="choice-input"
                  name={`${uid}-rotation`}
                  value={String(r)}
                  checked={draft.rotation !== null && Math.abs(draft.rotation - r) < 1e-3}
                  onChange={() => setRawDraft({ ...draft, rotation: r })}
                  data-testid={`rotation-${rotations.indexOf(r)}`}
                />
                <span className="choice-text">{rotationLabel(r)}</span>
              </label>
            ))}
          </div>
        </fieldset>
      ) : null}

      {preview && chosenItem ? (
        <div ref={previewRef} className="notice notice-warning placement-preview" role="status" data-testid="placement-preview">
          <strong>Preview — not placed yet:</strong> {name(chosenItem.itemId)} at {anchorLabel(preview.anchorId)}, {rotationLabel(preview.rotation).toLowerCase()}.
          {town && !canSee ? <div className="small">{tab === "yard" ? "The ghost shows when you are on the street." : "The ghost shows inside your home."}</div> : null}
          <div className="row" style={{ marginTop: 6 }}>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              disabled={busy || Boolean(placeBlocked)}
              aria-describedby={placeBlocked ? `${uid}-blocked` : undefined}
              onClick={() => void place()}
              data-testid="place-confirm"
            >
              {busy ? "Placing…" : "Place"}
            </button>
            <button
              type="button"
              className="btn btn-sm"
              disabled={busy}
              onClick={() => {
                setRawDraft(EMPTY_DRAFT);
                setMessage(null);
              }}
              data-testid="place-cancel"
            >
              Cancel
            </button>
          </div>
          {placeBlocked ? (
            <span id={`${uid}-blocked`} className="hint">
              {placeBlocked}
            </span>
          ) : null}
        </div>
      ) : null}

      {message ? (
        <div
          className={`notice small ${message.tone === "success" ? "notice-info" : message.tone === "warning" ? "notice-warning" : "notice-danger"}`}
          role={message.tone === "success" ? "status" : "alert"}
          data-testid="my-home-message"
        >
          {message.text}
        </div>
      ) : null}

      <section className="section" aria-label={tab === "yard" ? "Placed in your yard" : "Placed inside"}>
        <div className="section-title">{tab === "yard" ? "In your yard" : "Inside your home"}</div>
        {placed.length === 0 ? (
          <p className="muted small" style={{ margin: 0 }}>
            Nothing placed yet.
          </p>
        ) : (
          <ul className="history-list" data-testid="placed-list">
            {placed.map((p) => {
              const anchor = anchors.find((a) => a.id === p.anchorId);
              const canRotate = anchor ? fittingRotations(anchor, p.itemId).length > 1 && Boolean(instanceAt(p.anchorId)) : false;
              return (
                <li key={p.anchorId} className="row" style={{ justifyContent: "space-between" }} data-testid={`placed-${p.anchorId}`}>
                  <span className="small">
                    <strong>{name(p.itemId)}</strong> · {anchorLabel(p.anchorId)} · {rotationLabel(p.rotation).toLowerCase()}
                  </span>
                  <span className="row" style={{ gap: 4 }}>
                    {canRotate ? (
                      <button type="button" className="btn btn-sm" disabled={busy} onClick={() => void rotate(p.anchorId, p.itemId, p.rotation)} data-testid={`rotate-${p.anchorId}`}>
                        Rotate
                      </button>
                    ) : null}
                    <button type="button" className="btn btn-sm" disabled={busy} onClick={() => void remove(p.anchorId, p.itemId)} data-testid={`remove-${p.anchorId}`}>
                      Remove
                    </button>
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <section className="section" aria-label="In storage">
        <div className="section-title">In storage</div>
        {stored.length === 0 ? (
          <p className="small muted" style={{ margin: 0 }} data-testid="storage-empty">
            Nothing {tab === "yard" ? "for the yard" : "for inside"} in storage. {shopAction(shop)}
          </p>
        ) : (
          <ul className="history-list" data-testid="storage-list">
            {stored.map((item) => (
              <li key={item.inventoryInstanceId} className="row" style={{ justifyContent: "space-between" }}>
                <span className="small">{name(item.itemId)}</span>
                <button
                  type="button"
                  className={`btn btn-sm${placeBlocked && town ? " btn-primary" : ""}`}
                  disabled={busy || Boolean(town?.transitionPending)}
                  onClick={() => startWith(item)}
                  title={placeBlocked && town ? "Furniture goes inside your home: this takes you there and starts placing it." : undefined}
                  data-testid={`place-from-storage-${item.inventoryInstanceId}`}
                >
                  {placeBlocked && town ? "Place at home" : "Place…"}
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="section" aria-label="Visitors" data-testid="my-home-visitors">
        <VisitorAccessSwitch access={home.access} disabled={busy} onChange={(next) => void changeAccess(next)} />
        <p className="hint" style={{ margin: "4px 0 0" }}>
          You can always go in. Closed also keeps your patio set, hot tub and loungers for you. Your home and yard still show on the street.
        </p>
        <SendVisitorsOutView visitorsInside={visitorsInside ?? null} busy={busy} onSend={() => void sendVisitorsOut()} />
      </section>

      <section className="section" aria-label="Reset layout">
        <button type="button" className="btn btn-sm" disabled={busy || totalPlaced === 0} onClick={() => setConfirmReset(true)} data-testid="reset-layout">
          Reset layout
        </button>
        <span className="hint"> Moves every placed item (yard and inside) back to storage. Nothing is deleted.</span>
      </section>

      {confirmReset ? (
        <Dialog
          title="Reset layout?"
          onClose={() => setConfirmReset(false)}
          describedBy={`${uid}-reset`}
          footer={
            <>
              <button type="button" className="btn btn-primary" onClick={() => void reset()} data-testid="reset-confirm">
                Reset layout
              </button>
              <button type="button" className="btn" onClick={() => setConfirmReset(false)}>
                Cancel
              </button>
            </>
          }
        >
          <p id={`${uid}-reset`} style={{ margin: 0 }}>
            Items go back to storage. Nothing is deleted. All {totalPlaced} placed {totalPlaced === 1 ? "item" : "items"} in your yard and inside your
            home will move to storage, ready to place again.
          </p>
        </Dialog>
      ) : null}
    </div>
  );
}
